/**
 * Core vessel helper functions (≤400 lines, no legacy fields).
 * DO NOT read legacy fields (e.g., vessel.vesselType, areaCategory).
 * Canonical fields: category, type, grossTonnage, lengthOverall, passengerCapacity, crewCount, age, operations[].
 */

export const getCategory = (v) => {
  const raw = v?.category;
  if (typeof raw === 'string') {
    const m = raw.match(/cat(\d)/i);
    if (m) return parseInt(m[1], 10);
    const n = parseInt(raw, 10);
    return Number.isNaN(n) ? 6 : n;
  }
  const n = parseInt(raw ?? 6, 10);
  return Number.isNaN(n) ? 6 : n;
};

export const getVesselType = (v) => v?.type || 'workboat';

export const getTonnage = (v) => parseInt(v?.grossTonnage ?? 0, 10) || 0;
export const getLength = (v) => parseFloat(v?.lengthOverall ?? 0) || 0;
export const getPaxCount = (v) => parseInt(v?.passengerCapacity ?? v?.passengers ?? 0, 10) || 0;
export const getCrewCount = (v) => parseInt(v?.crewCount ?? v?.crew ?? 0, 10) || 0;
export const getTotalPersons = (v) => {
  // If pob (persons on board) is explicitly provided, use it
  if (v?.pob !== undefined) return parseInt(v.pob, 10) || 0;
  if (v?.personsOnBoard !== undefined) return parseInt(v.personsOnBoard, 10) || 0;
  // Otherwise calculate from crew + passengers
  return getCrewCount(v) + getPaxCount(v);
};
export const getAge = (v) => parseInt(v?.age ?? 0, 10) || 0;
export const hasOperation = (v, op) => Array.isArray(v?.operations) && v.operations.includes(op);

// Engine helpers
export const getEnginePower = (v) => Number(v?.totalEnginePower ?? v?.enginePower ?? v?.engine?.kW ?? 0);
export const getEngineLocation = (v) => v?.engineLocation ?? v?.engine?.location ?? 'unknown';
export const getLengthMeters = (v) => Number(v?.lengthOverall ?? v?.length ?? v?.length_m ?? 0);
export const getGrossTonnage = (v) => Number(v?.grossTonnage ?? v?.gt ?? 0);
export const getMaxSpeedKnots = (v) => Number(v?.maxSpeedKnots ?? v?.speed_kn ?? v?.speed ?? 0);

// Gate Logic v1.1 helpers
export const getChildrenCount = (v) => Number(v?.pobChildren ?? v?.children ?? v?.passengers?.children ?? 0);
export const getInfantsCount = (v) => Number(v?.pobInfants ?? v?.infants ?? v?.passengers?.infants ?? 0);
export const passengerVessel = (v) => 
  !!(v?.passengerVessel || v?.isPassengerVessel || v?.type === 'passenger' || 
     v?.vesselType === 'passenger' || v?.operationType === 'passenger' ||
     v?.vesselClass === 'passenger' || v?.passengers?.farePaying || v?.carriesPassengers);

// Parametric helpers
export const hasFixedMedia = (v, media) => {
  const normalizedMedia = media.toLowerCase();
  if (Array.isArray(v?.fixedFireMedia)) {
    return v.fixedFireMedia.some(m => m.toLowerCase() === normalizedMedia);
  }
  if (typeof v?.fixedFireMedia === 'string') {
    return v.fixedFireMedia.toLowerCase() === normalizedMedia;
  }
  // Check specific system flags
  return v[`has${media}System`] === true || 
         v[`has${normalizedMedia}System`] === true ||
         v[`has${media.toUpperCase()}System`] === true;
};
export const hasFixedActivation = (v, t) => (v?.fixedActivation === t) || (Array.isArray(v?.fixedActivation) && v.fixedActivation.includes(t));
export const hasDetectorType = (v, t) => Array.isArray(v?.detectors) && v.detectors.includes(t);
export const hasFuelType = (v, t) => (v?.fuelType === t) || (Array.isArray(v?.fuels) && v.fuels.includes(t));
export const hasGMDSS = (v, area) => Array.isArray(v?.gmdssAreas) && v.gmdssAreas.includes(area);

// Category helpers
export const isCat = (v, n) => Number(v?.category) === Number(n);
export const isCatAtLeast = (v, n) => Number(v?.category) >= Number(n);
export const isCatAtMost = (v, n) => Number(v?.category) <= Number(n);

// Regime helpers
export const isSeagoing = (v) => v?.regime === 'seagoing' || v?.category !== undefined;
export const isInland = (v) => v?.regime === 'inland' || v?.waterway === 'inland';

// Boolean helpers
// Engine/Propulsion
export const hasEngine = (v) =>
  Array.isArray(v?.engines) ? v.engines.length > 0 : !!(v?.engine || v?.enginePower);

export const hasInboard = (v) =>
  (Array.isArray(v?.engines) && v.engines.some(e => (e.type||e.arrangement)==='inboard')) ||
  v?.hasInboard || v?.engineType === 'inboard' || v?.engineArrangement === 'inboard';

export const hasOutboard = (v) =>
  (Array.isArray(v?.engines) && v.engines.some(e => (e.type||e.arrangement)==='outboard')) ||
  v?.hasOutboard || v?.engineType === 'outboard' || v?.engineArrangement === 'outboard';

// Steering/Control
export const hasSteering = (v) =>
  !!(v?.steering?.type || v?.rudder || v?.tiller || v?.hasSteering);

export const hasRemoteControl = (v) =>
  !!(v?.steering?.remoteControl || v?.helmStations > 1 || v?.remoteSteering);

export const hasRudder = (v) => !!(v?.steering?.rudder || v?.rudder || v?.hasRudder);
export const hasRudderStock = (v) => !!(v?.steering?.rudderStock || v?.rudderStock);
export const hasAutopilot = (v) => !!(v?.steering?.autopilot || v?.autopilot);
export const hasAccommodationSpaces = (v) => v?.hasAccommodation || v?.spaces?.includes('accommodation') || v?.accommodationSpaces > 0;
export const hasSleepingSpaces = (v) => v?.hasSleeping || v?.spaces?.includes('sleeping') || v?.sleepingSpaces > 0;
export const hasCookingAppliance = (v) => v?.hasCooking || v?.spaces?.includes('galley') || v?.galleyType;
export const hasOpenFlame = (v) => v?.hasOpenFlame || v?.cookingType === 'gas' || v?.heatingType === 'gas';
export const hasExposedLocation = (v) => v?.hasExposedLocation || v?.vesselType === 'open-boat';
export const hasMachinerySpace = (v) => v?.hasMachinerySpace || v?.spaces?.includes('machinery');
export const hasStorageRoom = (v) => v?.hasStorageRoom || v?.spaces?.includes('storage');
export const hasFixedFireSystem = (v) => v?.hasFixedFireSystem || (v?.fixedFireType && v?.fixedFireType !== 'none');
export const hasFireBlanket = (v) => v?.hasFireBlanket || hasCookingAppliance(v);
export const hasFireBucket = (v) => v?.hasFireBucket || true; // Most vessels have them
export const hasFirePump = (v) => v?.hasFirePump || !isOpenBoat(v);
export const hasExtinguisherLocker = (v) => v?.hasExtinguisherLocker || v?.extinguisherStorage === 'locker';
export const hasEscapeHatch = (v) => v?.hasEscapeHatch || v?.emergencyEscape?.includes('hatch');
export const hasEscapeRoutes = (v) => v?.hasEscapeRoutes || v?.emergencyEscape?.length > 0;
export const hasNavLights = (v) => v?.hasNavLights !== false; // Default to true
export const hasLPG = (v) => v?.hasLPG || v?.fuelType?.includes('lpg') || v?.gasSystem === 'lpg';
export const hasEEBD = (v) => v?.hasEEBD || v?.emergencyEscape?.includes('eebd');
export const hasMES = (v) => v?.hasMES || v?.evacuationSystem === 'mes';
export const hasAED = (v) => v?.hasAED || v?.medicalEquipment?.includes('aed');
export const hasRaft = (v) => v?.hasRaft || v?.liferafts > 0;
export const hasRescueBoat = (v) => v?.hasRescueBoat || v?.rescueBoats > 0;
export const hasFreeFall = (v) => v?.hasFreeFall || v?.lifeboatType === 'freefall';
export const hasFirePort = (v) => v?.hasFirePort || (v?.engineLocation === 'engine-box' && !hasFixedFireSystem(v));
export const hasMarineExtinguisher = (v) => v?.extinguisherMarine !== false; // Default to true
export const isOpenBoat = (v) => v?.isOpenBoat || v?.vesselType === 'open-boat' || v?.hullType === 'open';

// Additional core helpers for machinery
export const hasCCTV = (v) => !!(v?.hasCCTV || v?.securityEquipment?.includes('cctv'));
export const hasHazardousZone = (v) => !!(v?.hasHazardousZone || v?.hazardousZones?.length > 0);
export const isHighSpeed = (v) => getMaxSpeedKnots(v) > 30;
export const internationalVoyage = (v) => !!(v?.operations?.internationalVoyages || v?.internationalVoyage || v?.hasInternationalVoyages);

// Phase 1 Token Additions (Sept 2025)
export const hasElectricalSystem = (v) => {
  // Check various ways electrical system might be specified
  return !!(v?.hasElectricalSystem || 
           v?.electrical?.system || 
           v?.electricalSystem || 
           v?.hasElectrical ||
           v?.power?.source === 'electrical' ||
           v?.power?.batteries ||
           v?.batteries?.length > 0);
};

export const hasPortableExtinguisher = (v) => {
  // Check for portable fire extinguishers
  return !!(v?.hasPortableExtinguisher ||
           v?.ffe?.extinguishers?.portable > 0 ||
           v?.fireExtinguishers?.portable > 0 ||
           v?.portableExtinguishers > 0 ||
           v?.extinguishers?.some(e => e.type === 'portable') ||
           v?.fireEquipment?.includes('portable-extinguisher'));
};

export const hasFuelSystem = (v) => {
  // Note: this already exists above, but ensure it covers all cases
  return !!(v?.hasFuelSystem || 
           v?.machinery?.fuelSystem ||
           v?.fuel?.system ||
           v?.fuelTanks?.length > 0 ||
           v?.engines?.length > 0); // If has engines, must have fuel system
};

export const seaAreaA1 = (v) => {
  // Check if vessel operates in GMDSS Sea Area A1
  return !!(v?.gmdss?.area === 'A1' ||
           v?.gmdss?.areas?.includes('A1') ||
           v?.seaAreas?.includes('A1') ||
           v?.operatingArea === 'A1' ||
           hasGMDSS(v, 'A1'));
};

// Phase 2 Token Additions - Added 2025-09-16
export const seaAreaA2 = (v) => {
  // Check if vessel operates in GMDSS Sea Area A2
  return !!(v?.gmdss?.area === 'A2' ||
           v?.gmdss?.areas?.includes('A2') ||
           v?.seaAreas?.includes('A2') ||
           v?.operatingArea === 'A2' ||
           hasGMDSS(v, 'A2'));
};

export const seaAreaA3 = (v) => {
  // Check if vessel operates in GMDSS Sea Area A3
  return !!(v?.gmdss?.area === 'A3' ||
           v?.gmdss?.areas?.includes('A3') ||
           v?.seaAreas?.includes('A3') ||
           v?.operatingArea === 'A3' ||
           hasGMDSS(v, 'A3'));
};

export const hasFireDetection = (v) => {
  // Check if vessel has fire detection system
  return !!(v?.hasFireDetection ||
           v?.electrical?.fireDetection ||
           v?.fireDetection ||
           v?.detectionSystem ||
           v?.ffe?.detection ||
           v?.safety?.detection);
};

export const hasBatteryBank = (v) => {
  // Check if vessel has battery bank for electrical power
  return !!(v?.hasBatteryBank ||
           v?.electrical?.batteryBankCount > 0 ||
           v?.batteryBanks?.length > 0 ||
           v?.batteries?.bank ||
           v?.power?.batteryBank);
};

export const shorePowerAvailable = (v) => {
  // Check if vessel has shore power connection available
  return !!(v?.shorePowerAvailable ||
           v?.electrical?.shorePower ||
           v?.shorePower ||
           v?.power?.shore ||
           v?.connections?.shorePower);
};

export const hasLPGSystem = (v) => {
  // Check if vessel has LPG (liquefied petroleum gas) system
  return !!(v?.hasLPGSystem ||
           v?.environmental?.lpgSystem ||
           v?.lpg?.system ||
           v?.gas?.lpg ||
           v?.fuel?.lpg ||
           hasLPG(v)); // Use existing hasLPG helper if available
};

// Removed duplicate - passengerVessel already defined at line 45

export const hasCrane = (v) => {
  // Check if vessel has crane equipment
  return !!(v?.hasCrane ||
           v?.equipment?.crane ||
           v?.lifting?.crane ||
           v?.deck?.crane ||
           v?.operations?.crane);
};

export const hasGMDSSEquipment = (v) => {
  // Check if vessel has GMDSS equipment installed (renamed from hasGMDSS to avoid conflict)
  return !!(v?.hasGMDSSEquipment ||
           v?.hasGMDSS ||
           v?.gmdss?.equipment ||
           v?.radio?.gmdss ||
           v?.communications?.gmdss ||
           v?.gmdss?.area); // If has GMDSS area, must have equipment
};

// Phase 3 Token Helpers (FFE Domain)
// hasFireBlanket already defined at line 109

export const hasFireBuckets = (v) => {
  // Check if vessel has fire buckets with sand
  return !!(v?.hasFireBuckets ||
           v?.ffe?.fireBuckets ||
           v?.fireEquipment?.buckets ||
           v?.fireEquipment?.sandBuckets ||
           v?.deckEquipment?.fireBuckets);
};

export const hasFireHose = (v) => {
  // Check if vessel has fire hose and nozzles
  return !!(v?.hasFireHose ||
           v?.ffe?.fireHose ||
           v?.fireEquipment?.hose ||
           v?.firePump?.hose ||
           v?.firefighting?.hose);
};

// Phase 3 Token Helpers (ELECTRICAL Domain)
export const hasCentralElectrical = (v) => {
  // Check if vessel has centralized electrical system
  return !!(v?.hasCentralElectrical ||
           v?.electrical?.centralized ||
           v?.electrical?.mainPanel ||
           v?.electrical?.distribution ||
           v?.powerSystems?.centralized);
};

export const hasBackupBatteries = (v) => {
  // Check if vessel has backup battery systems
  return !!(v?.hasBackupBatteries ||
           v?.electrical?.backupBatteries ||
           v?.emergency?.batteries ||
           v?.batteries?.backup ||
           v?.powerSystems?.backup);
};

export const hasEmergencyPower = (v) => {
  // Check if vessel has emergency power source
  return !!(v?.hasEmergencyPower ||
           v?.electrical?.emergencyPower ||
           v?.emergency?.power ||
           v?.emergencyGenerator ||
           v?.powerSystems?.emergency);
};

// Phase 3 Token Helpers (SURVEYS Domain)
export const hasSelfExamAuth = (v) => {
  // Check if vessel has self-examination authority
  return !!(v?.hasSelfExamAuth ||
           v?.survey?.selfExamination ||
           v?.certification?.selfExam ||
           v?.inspections?.selfExam ||
           v?.authority?.selfExamination);
};

export const hasDeficiencies = (v) => {
  // Check if vessel has outstanding deficiencies
  return !!(v?.hasDeficiencies ||
           v?.survey?.deficiencies ||
           v?.inspection?.deficiencies ||
           v?.defects?.outstanding ||
           v?.compliance?.deficiencies);
};

export const hasInWaterExam = (v) => {
  // Check if vessel requires in-water examination
  return !!(v?.hasInWaterExam ||
           v?.survey?.inWater ||
           v?.inspection?.underwater ||
           v?.hull?.inWaterExam ||
           v?.survey?.underwaterExam);
};

// Phase 3 Token Helpers (Machinery/FFE Domain)
export const hasInboardEngine = (v) => {
  // Check if vessel has inboard engine installation
  return !!(v?.hasInboardEngine ||
           v?.hasInboard ||
           v?.engineType === 'inboard' ||
           v?.propulsion?.inboard ||
           v?.machinery?.inboard);
};

export const hasFixedCO2 = (v) => {
  // Check if vessel has fixed CO2 fire suppression system
  return !!(v?.hasFixedCO2 ||
           v?.ffe?.fixedCO2 ||
           v?.fixedFireSystem?.co2 ||
           v?.machinerySpace?.co2System ||
           v?.firefighting?.fixedCO2);
};

export const hasFixedAerosol = (v) => {
  // Check if vessel has fixed aerosol fire suppression system
  return !!(v?.hasFixedAerosol ||
           v?.ffe?.fixedAerosol ||
           v?.fixedFireSystem?.aerosol ||
           v?.machinerySpace?.aerosolSystem ||
           v?.firefighting?.fixedAerosol);
};

// Phase 4 Token Helpers (CREW Domain)
export const hasHighSpeedOps = (v) => {
  // Check if vessel conducts high speed operations
  return !!(v?.hasHighSpeedOps ||
           v?.operations?.highSpeed ||
           v?.maxSpeed > 30 ||
           v?.isHighSpeed ||
           v?.vesselType === 'hscraft');
};

export const hasFoodPreparation = (v) => {
  // Check if vessel has food preparation facilities
  return !!(v?.hasFoodPreparation ||
           v?.galley ||
           v?.hasCookingAppliance ||
           v?.cooking ||
           v?.catering?.enabled);
};

export const hasVHF = (v) => {
  // Check if vessel has VHF radio equipment
  return !!(v?.hasVHF ||
           v?.radio?.vhf ||
           v?.communications?.vhf ||
           v?.gmdss?.vhf ||
           v?.equipment?.vhfRadio);
};

// hasGMDSS already defined at line 67 with area parameter

export const getMinimumCrew = (v) => {
  // Get vessel's minimum crew requirement
  return v?.minimumCrew || 
         v?.manning?.minimum ||
         v?.crew?.minimum ||
         v?.safeManningLevel ||
         1; // Default to 1 if not specified
};

// Phase 4 Token Helpers (ENVIRONMENTAL Domain)
export const hasInternationalVoyage = (v) => {
  // Check if vessel makes international voyages
  return !!(v?.hasInternationalVoyage ||
           v?.internationalVoyage ||
           v?.operations?.international ||
           v?.voyages?.international ||
           v?.tradingArea === 'international');
};

export const hasProhibitedDischargeArea = (v) => {
  // Check if vessel operates in prohibited discharge areas
  return !!(v?.hasProhibitedDischargeArea ||
           v?.environmental?.prohibitedArea ||
           v?.operations?.specialArea ||
           v?.marpol?.specialArea ||
           v?.dischargeRestrictions);
};

export const hasIMOSewageSystem = (v) => {
  // Check if vessel has IMO-approved sewage treatment system
  return !!(v?.hasIMOSewageSystem ||
           v?.sewage?.imoApproved ||
           v?.environmental?.sewageTreatment ||
           v?.marpol?.sewageSystem ||
           v?.blackWater?.treatment);
};

export const hasOilySludge = (v) => {
  // Check if vessel produces oily sludge requiring disposal
  return !!(v?.hasOilySludge ||
           v?.machinery?.oilySludge ||
           v?.environmental?.sludge ||
           v?.marpol?.sludge ||
           v?.wasteOil?.production);
};

// Numeric comparison helpers
// getTotalPersons already defined at line 25

// getLengthMeters already defined at line 38

// Constants (centralise magic numbers)
export const GT_AIS_THRESHOLD = 300;
export const LOA_FIRE_AXE_M = 15;

// ========================================
// Phase 5 helpers - SURVEYS, GMDSS, FFE domains
// ========================================

// SURVEYS domain helpers
export const hasIncident = (v) => {
  return !!(v?.hasIncident ||
           v?.incidents?.history ||
           v?.safety?.incidents ||
           v?.accidents?.recorded);
};

export const hasEmergencyExam = (v) => {
  return !!(v?.hasEmergencyExam ||
           v?.surveys?.emergencyExam ||
           v?.examinations?.emergency ||
           v?.inspections?.emergency);
};

export const hasConstructionSurvey = (v) => {
  return !!(v?.hasConstructionSurvey ||
           v?.surveys?.construction ||
           v?.newBuild?.survey ||
           v?.construction?.inspection);
};

export const hasCATransfer = (v) => {
  return !!(v?.hasCATransfer ||
           v?.certification?.caTransfer ||
           v?.authority?.transfer ||
           v?.registration?.transfer);
};

// GMDSS domain helpers
export const hasFixedRadio = (v) => {
  return !!(v?.hasFixedRadio ||
           v?.gmdss?.fixedRadio ||
           v?.radio?.fixed ||
           v?.comms?.fixedRadio);
};

export const hasPortableVHF = (v) => {
  return !!(v?.hasPortableVHF ||
           v?.gmdss?.portableVHF ||
           v?.radio?.portable ||
           v?.comms?.handheld);
};

export const hasFixedVHF = (v) => {
  return !!(v?.hasFixedVHF ||
           v?.gmdss?.fixedVHF ||
           v?.radio?.vhf ||
           v?.comms?.vhfFixed);
};

export const hasMF = (v) => {
  return !!(v?.hasMF ||
           v?.gmdss?.mfRadio ||
           v?.radio?.mf ||
           v?.comms?.mediumFrequency);
};

export const hasDSC = (v) => {
  return !!(v?.hasDSC ||
           v?.gmdss?.dsc ||
           v?.radio?.digitalCall ||
           v?.comms?.dsc);
};

export const hasROC = (v) => {
  return !!(v?.hasROC ||
           v?.crew?.rocCertified ||
           v?.manning?.rocOperator ||
           v?.certificates?.roc);
};

export const hasRadar = (v) => {
  return !!(v?.hasRadar ||
           v?.navigation?.radar ||
           v?.electronics?.radar ||
           v?.equipment?.radar);
};

export const hasRadioEquipment = (v) => {
  return !!(v?.hasRadioEquipment ||
           v?.gmdss?.equipped ||
           v?.radio?.any ||
           v?.comms?.radio);
};

// FFE domain helpers  
// hasExtinguisherLocker already defined at line 112

export const hasPowerPump = (v) => {
  return !!(v?.hasPowerPump ||
           v?.ffe?.powerPump ||
           v?.fire?.enginePump ||
           v?.pumps?.firePower);
};

export const hasNonMarineExtinguisher = (v) => {
  return !!(v?.hasNonMarineExtinguisher ||
           v?.ffe?.nonMarine ||
           v?.fire?.domesticExtinguisher ||
           v?.safety?.nonApproved);
};

export const hasExcessEquipment = (v) => {
  return !!(v?.hasExcessEquipment ||
           v?.ffe?.excess ||
           v?.fire?.surplus ||
           v?.equipment?.redundant);
};

export const hasHighFireRisk = (v) => {
  return !!(v?.hasHighFireRisk ||
           v?.risk?.fireHigh ||
           v?.hazards?.fire ||
           v?.operations?.highRiskFire);
};

export const hasLithiumDetection = (v) => {
  return !!(v?.hasLithiumDetection ||
           v?.ffe?.lithiumDetector ||
           v?.fire?.lithiumMonitor ||
           v?.safety?.batteryDetection);
};

// Vessel type helpers (Phase 5)
export const isInflatable = (v) => {
  return !!(v?.isInflatable ||
           v?.type === 'inflatable' ||
           v?.hull?.inflatable ||
           v?.construction?.inflatable);
};

export const isRIB = (v) => {
  return !!(v?.isRIB ||
           v?.type === 'rib' ||
           v?.vesselType === 'rib' ||
           v?.hull?.rigidInflatable);
};

export const isROUV = (v) => {
  return !!(v?.isROUV ||
           v?.type === 'rouv' ||
           v?.unmanned?.operated ||
           v?.remote?.vehicle);
};

export const isManned = (v) => {
  return !!(v?.isManned ||
           v?.crewed ||
           !v?.unmanned ||
           v?.manning?.required);
};

export const isTransitioning = (v) => {
  return !!(v?.isTransitioning ||
           v?.status?.transitioning ||
           v?.operations?.transition ||
           v?.voyage?.delivery);
};

export const operatesInlandOnly = (v) => {
  return !!(v?.operatesInlandOnly ||
           v?.operations?.inlandOnly ||
           v?.area?.inlandWaterways ||
           !v?.seagoing);
};

// Numeric helper functions (Phase 5)
export const getVesselAge = (v) => {
  // Returns vessel age in years
  const currentYear = new Date().getFullYear();
  const buildYear = v?.vesselAge ||
                   v?.yearBuilt ||
                   v?.construction?.year ||
                   v?.build?.year;
  
  if (buildYear && !isNaN(buildYear)) {
    return currentYear - buildYear;
  }
  return 0;
};

export const getVoyageDuration = (v) => {
  // Returns voyage duration in hours
  return v?.voyageDuration ||
         v?.voyage?.hours ||
         v?.operations?.voyageHours ||
         v?.planning?.duration ||
         0;
};

export const getCrewChangePercentage = (v) => {
  // Returns crew change percentage
  return v?.crewChangePercentage ||
         v?.crew?.changeRate ||
         v?.manning?.turnover ||
         v?.hr?.changePercent ||
         0;
};

// Enum helper functions (Phase 5)
export const hasMediaType = (v, mediaType) => {
  // Check for specific fire extinguisher media type
  const mediaTypes = v?.mediaTypes || 
                    v?.ffe?.media || 
                    v?.fire?.extinguisherTypes ||
                    v?.equipment?.fireMedia ||
                    [];
  
  return Array.isArray(mediaTypes) ? 
         mediaTypes.includes(mediaType) : 
         mediaTypes === mediaType;
};

export const hasMaintenanceOption = (v, option) => {
  // Check for specific maintenance option
  const maintenanceOptions = v?.maintenanceOptions ||
                            v?.maintenance?.options ||
                            v?.service?.arrangements ||
                            [];
  
  return Array.isArray(maintenanceOptions) ?
         maintenanceOptions.includes(option) :
         maintenanceOptions === option;
};

export const isClass = (v, classType) => {
  // Check for vessel classification
  return v?.classification === classType ||
         v?.class === classType ||
         v?.society?.class === classType ||
         v?.register?.class === classType;
};

// ========================================
// Phase 6 helpers - Final cleanup for 95%+ coverage
// ========================================

// ELECTRICAL domain helpers
export const hasElectricalNavEquipment = (v) => {
  return !!(v?.hasElectricalNavEquipment ||
           v?.electrical?.navEquipment ||
           v?.navigation?.electrical ||
           v?.electronics?.navigation);
};

export const hasBatteryCharger = (v) => {
  return !!(v?.hasBatteryCharger ||
           v?.electrical?.charger ||
           v?.battery?.charger ||
           v?.power?.charging);
};

export const hasVentilatedBatteries = (v) => {
  return !!(v?.hasVentilatedBatteries ||
           v?.battery?.ventilated ||
           v?.electrical?.ventilation ||
           v?.power?.ventilated);
};

export const hasHighVoltage = (v) => {
  return !!(v?.hasHighVoltage ||
           v?.electrical?.highVoltage ||
           v?.power?.highVoltage ||
           v?.voltage?.high);
};

export const hasBatteries = (v) => {
  return !!(v?.hasBatteries ||
           v?.electrical?.batteries ||
           v?.power?.battery ||
           v?.battery?.installed);
};

// FFE/Machinery domain helpers
export const hasTwoBilgePumps = (v) => {
  return !!(v?.hasTwoBilgePumps ||
           v?.bilge?.dualPumps ||
           v?.pumps?.bilgeCount >= 2 ||
           v?.machinery?.twoBilgePumps);
};

export const hasAutoActivation = (v) => {
  return !!(v?.hasAutoActivation ||
           v?.fire?.autoActivation ||
           v?.ffe?.automatic ||
           v?.suppression?.auto);
};

export const cylindersInMachinery = (v) => {
  return !!(v?.cylindersInMachinery ||
           v?.gas?.cylindersInEngine ||
           v?.machinery?.gasCylinders ||
           v?.storage?.cylindersInMachinery);
};

// hasRemoteControl already defined at line 95

export const hasRemoteFuelIsolation = (v) => {
  return !!(v?.hasRemoteFuelIsolation ||
           v?.fuel?.remoteIsolation ||
           v?.machinery?.fuelCutoff ||
           v?.safety?.remoteFuelShutoff);
};

export const hasHazardousSpaces = (v) => {
  return !!(v?.hasHazardousSpaces ||
           v?.spaces?.hazardous ||
           v?.areas?.dangerous ||
           v?.compartments?.hazardous);
};

export const hasOilLubricatedShaft = (v) => {
  return !!(v?.hasOilLubricatedShaft ||
           v?.shaft?.oilLubricated ||
           v?.propulsion?.oilShaft ||
           v?.machinery?.oilShaft);
};

export const hasWorkboatPilotEndorsement = (v) => {
  return !!(v?.hasWorkboatPilotEndorsement ||
           v?.crew?.pilotEndorsement ||
           v?.manning?.workboatPilot ||
           v?.certificates?.workboatPilot);
};

export const hasOpenSteeringPosition = (v) => {
  return !!(v?.steeringPosition === 'open' ||
           v?.steeringPosition === 'both' ||
           v?.hasOpenSteeringPosition);
};

export const hasElectricStart = (v) => {
  return !!(v?.engineStartMethod === 'electric' ||
           v?.hasElectricStart ||
           v?.engineType === 'diesel'); // Most diesel engines have electric start
};

export const hasWaterSeparatorBowl = (v) => {
  return !!(v?.hasWaterSeparatorBowl ||
           v?.fuel?.waterSeparator ||
           v?.filters?.waterBowl ||
           v?.machinery?.fuelWaterSeparator);
};

// OPERATIONS domain helpers
export const hasLiftingDevice = (v) => {
  return !!(v?.hasLiftingDevice ||
           v?.lifting?.device ||
           v?.equipment?.lifting ||
           v?.operations?.liftingGear);
};

export const hasDiverLift = (v) => {
  return !!(v?.hasDiverLift ||
           v?.diving?.lift ||
           v?.equipment?.diverLift ||
           v?.operations?.diverRecovery);
};

export const hasDiveBasket = (v) => {
  return !!(v?.hasDiveBasket ||
           v?.diving?.basket ||
           v?.equipment?.diveBasket ||
           v?.operations?.divingBasket);
};

export const hasDiveLadder = (v) => {
  return !!(v?.hasDiveLadder ||
           v?.diving?.ladder ||
           v?.equipment?.diveLadder ||
           v?.access?.diveLadder);
};

export const hasVariableLoad = (v) => {
  return !!(v?.hasVariableLoad ||
           v?.loading?.variable ||
           v?.cargo?.variable ||
           v?.operations?.variableLoad);
};

export const hasHydraulicCrane = (v) => {
  return !!(v?.hasHydraulicCrane ||
           v?.crane?.hydraulic ||
           v?.lifting?.hydraulicCrane ||
           v?.equipment?.hydraulicCrane);
};

export const hasMarinisedCrane = (v) => {
  return !!(v?.hasMarinisedCrane ||
           v?.crane?.marinised ||
           v?.lifting?.marineCrane ||
           v?.equipment?.marinisedCrane);
};

export const hasLandBasedCrane = (v) => {
  return !!(v?.hasLandBasedCrane ||
           v?.crane?.landBased ||
           v?.lifting?.shoreCrane ||
           v?.operations?.landCrane);
};

export const hasGantry = (v) => {
  return !!(v?.hasGantry ||
           v?.crane?.gantry ||
           v?.lifting?.gantrySystem ||
           v?.equipment?.gantry);
};

export const hasTransomLift = (v) => {
  return !!(v?.hasTransomLift ||
           v?.stern?.lift ||
           v?.transom?.platform ||
           v?.recovery?.transomLift);
};

export const hasNightDeckOps = (v) => {
  return !!(v?.hasNightDeckOps ||
           v?.operations?.nightDeck ||
           v?.work?.nighttime ||
           v?.deck?.nightOperations);
};

// GMDSS/Navigation helpers
export const hasMultipleLiferafts = (v) => {
  return !!(v?.hasMultipleLiferafts ||
           v?.lsa?.multipleRafts ||
           v?.liferafts?.count > 1 ||
           v?.safety?.multipleLiferafts);
};

export const ineffectiveNonGMDSS = (v) => {
  return !!(v?.ineffectiveNonGMDSS ||
           v?.radio?.nonGMDSSIneffective ||
           v?.gmdss?.ineffectiveAlternative ||
           v?.comms?.ineffectiveNonGMDSS);
};

export const canGetWeatherInfo = (v) => {
  return !!(v?.canGetWeatherInfo ||
           v?.comms?.weatherCapable ||
           v?.radio?.weatherReception ||
           v?.navigation?.weatherInfo);
};

export const hasPLB = (v) => {
  return !!(v?.hasPLB ||
           v?.safety?.plb ||
           v?.emergency?.personalBeacon ||
           v?.crew?.personalLocator);
};

export const hasECS = (v) => {
  return !!(v?.hasECS ||
           v?.navigation?.ecs ||
           v?.charts?.electronic ||
           v?.electronics?.chartSystem);
};

export const hasAutoTracking = (v) => {
  return !!(v?.hasAutoTracking ||
           v?.navigation?.autoTrack ||
           v?.radar?.autoTracking ||
           v?.electronics?.tracking);
};

export const hasDeviationRecord = (v) => {
  return !!(v?.hasDeviationRecord ||
           v?.compass?.deviationCard ||
           v?.navigation?.deviation ||
           v?.magnetic?.deviationRecord);
};

// Vessel type helpers
export const isSteel = (v) => {
  return !!(v?.isSteel ||
           v?.hull?.material === 'steel' ||
           v?.construction?.steel ||
           v?.material?.steel);
};

export const pilotBoat = (v) => {
  return !!(v?.pilotBoat ||
           v?.type === 'pilot' ||
           v?.vesselType === 'pilotBoat' ||
           v?.operations?.pilotage);
};

export const policeBoat = (v) => {
  return !!(v?.policeBoat ||
           v?.type === 'police' ||
           v?.vesselType === 'policeBoat' ||
           v?.operations?.lawEnforcement);
};

// Hazard helpers
export const possibleLEL = (v) => {
  return !!(v?.possibleLEL ||
           v?.hazards?.lel ||
           v?.gas?.explosiveLimits ||
           v?.atmosphere?.lel);
};

export const hasDangerousGases = (v) => {
  return !!(v?.hasDangerousGases ||
           v?.hazards?.gases ||
           v?.cargo?.dangerousGases ||
           v?.atmosphere?.toxic);
};

export const hasExtendedHeelApproval = (v) => {
  return !!(v?.hasExtendedHeelApproval ||
           v?.stability?.extendedHeel ||
           v?.approval?.heelAngle ||
           v?.certification?.extendedHeel);
};

export const hasLightningRisk = (v) => {
  return !!(v?.hasLightningRisk ||
           v?.risk?.lightning ||
           v?.hazards?.lightning ||
           v?.weather?.lightningRisk);
};

export const hasSleepingBunks = (v) => {
  return !!(v?.hasSleepingBunks ||
           v?.accommodation?.bunks ||
           v?.crew?.sleepingQuarters ||
           v?.spaces?.bunks);
};

// Numeric helpers for Phase 6
// getMinimumCrew already defined at line 386

export const getMaxSpeed = (v) => {
  return v?.maxSpeed ||
         v?.speed?.maximum ||
         v?.performance?.topSpeed ||
         v?.specifications?.speed ||
         0;
};
export const PERSONS_LIFERAFT = 16;